import { uniqueId } from "@/utils/fn";
import React, { useState, useCallback } from "react";
import { FieldOption } from ".";

interface Props {
  options: Array<FieldOption>;
  onChange(options: Array<FieldOption>): void;
}

export default function OptionsControl({ options, onChange }: Props) {
  const [state, setState] = useState<FieldOption>({
    label: "",
    value: "",
    id: uniqueId()
  });

  const removeOption = useCallback(
    (id: string) => {
      onChange(options.filter((x) => x.id !== id));
    },
    [options, onChange]
  );

  const addOption = useCallback(() => {
    if (!state.label.trim()) return;
    onChange([...options, state]);
    setState({ label: "", value: "", id: uniqueId() });
  }, [options, onChange, state]);

  function update(updates: Partial<FieldOption>) {
    setState((oldState) => ({
      ...oldState,
      ...updates
    }));
  }

  return (
    <div>
      <label className="block text-sm font-medium mb-1">Options</label>
      <div className="flex gap-2 mb-2 text-xs text-slate-500">
        <span className="w-2/5">Label</span>
        <span className="w-2/5">Value</span>
      </div>
      <div className="flex gap-2 mb-2">
        <input
          className="input w-2/5"
          type="text"
          placeholder="Option label"
          value={state.label}
          onChange={(e) => update({ label: e.currentTarget.value })}
        />
        <input
          className="input w-2/5"
          type="text"
          placeholder="Option value"
          value={state.value}
          onChange={(e) => update({ value: e.currentTarget.value })}
        />
        <button
          type="button"
          className="underline text-sm text-primary hover:text-primary-active w-1/5 text-center"
          onClick={addOption}
          disabled={!state.label.trim()}
          aria-label="Add option"
        >
          Add
        </button>
      </div>
      {options.length > 0 && (
        <div className="max-h-[120px] overflow-y-auto border border-slate-200 rounded">
          {options.map((opt) => (
            <div
              key={opt.id}
              className="flex items-center gap-2 px-2 py-1.5 border-b border-slate-100 last:border-b-0 text-sm"
            >
              <span className="w-2/5 truncate">{opt.label}</span>
              <span className="w-2/5 truncate text-slate-500">
                {opt.value || opt.label}
              </span>
              <button
                type="button"
                className="underline text-xs text-crimson-red-600 hover:text-crimson-red-700 w-1/5 text-center"
                onClick={() => removeOption(opt.id)}
                aria-label={`Remove option ${opt.label}`}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
